import {useForm} from "react-hook-form";
import * as React from "react";
import {useEffect} from "react";
import CoreForm from "../../../../../../web-v2/components/CoreForm";
import {TextFieldController} from "../../../../../../web-v2/components/forms/TextFieldController";
import {
  AssetManageForm as AssetForm,
  assetPriorityOptions
} from "../../../../../../../store/data/entities/asset";
import {
  useGetAllBuildingsQuery,
  useGetAllFloorsQuery,
  useGetAllLocationsQuery
} from "../../../../../../../store/services/api/building-api";
import {
  useGetAllAssetTypesQuery,
  useGetAllSubAssetTypesQuery
} from "../../../../../../../store/services/api/asset-type-api";
import {useSetLoading} from "../../../../../../../hooks/useSetLoading";
import * as DocumentPicker from "expo-document-picker";
import {PickerFieldController} from "../../../../../../web-v2/components/PickerFieldController";
import {Image, StyleSheet, View} from "react-native";
import {Button} from "react-native-paper";

interface AssetManageFormProps {
  asset: any;
  onSubmit: (data: any) => void;
  readOnly?: boolean;
}

export default function AssetManageForm({asset, onSubmit, readOnly = false}: AssetManageFormProps) {
  const { data: buildings, isFetching: buildingsFetching } = useGetAllBuildingsQuery(null);
  const { data: floors, isFetching: floorsFetching } = useGetAllFloorsQuery(null);
  const { data: locations, isFetching: locationsFetching } = useGetAllLocationsQuery(null);
  const { data: assetTypes, isFetching: assetTypesFetching } = useGetAllAssetTypesQuery(null);
  const { data: subAssetTypes, isFetching: subAssetTypesFetching } = useGetAllSubAssetTypesQuery(null);

  useSetLoading([buildingsFetching, floorsFetching, locationsFetching, assetTypesFetching, subAssetTypesFetching]);

  const form = useForm<AssetForm>({
    defaultValues: {
      id: undefined,
      buildingId: "",
      floorId: "",
      locationId: "",
      assetTypeId: "",
      subAssetTypeId: "",
      description: "",
      code: "",
      uniqueReference: "",
      imageBase64: "",
      troubleshootingGuide: "",
      priority: 0,
      supportingInformation: ""
    }
  });
  let imageBase64 = form.watch("imageBase64");
  const buildingId = form.watch('buildingId');
  const floorId = form.watch('floorId');
  const assetTypeId = form.watch('assetTypeId');

  useEffect(() => {
    if (asset) {
      form.setValue("id", asset.id);
      form.setValue("buildingId", asset.buildingId);
      form.setValue("floorId", asset.floorId);
      form.setValue("locationId", asset.locationId);
      form.setValue("assetTypeId", asset.assetTypeId);
      form.setValue("subAssetTypeId", asset.subAssetTypeId);
      form.setValue("description", asset.description);
      form.setValue("code", asset.code);
      form.setValue("supportingInformation", asset.supportingInformation);
      form.setValue("uniqueReference", asset.uniqueReference);
      form.setValue("troubleshootingGuide", asset.troubleshootingGuide);
      form.setValue("priority", asset.priority);
    }
  }, [asset])

  const handleOnSubmit = () => {
    form.handleSubmit(onSubmit)();
  }

  const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const loadFile = async () => {
    var result = await DocumentPicker.getDocumentAsync({
      type: ".png"
    }) as any;

    if (result.assets.length == 0) return;
    const file = result.assets[0].file;

    var base64 = (await toBase64(file)) as string;

    form.setValue("imageBase64", base64);
  }

  const floorOptions = (floors ?? []).filter(item => item.buildingId === buildingId).map(item => ({
    label: item.name,
    value: item.id
  }))

  const locationOptions = (locations ?? []).filter(item => item.floorId === floorId).map(item => ({
    label: item.name,
    value: item.id
  }))

  const subAssetTypeOptions = (subAssetTypes ?? []).filter(item => item.assetTypeId === assetTypeId).map(item => ({
    label: item.name,
    value: item.id
  }))

  const handleOnBuildingChange = () => {
    form.setValue('floorId', '');
    form.setValue('locationId', '');
  }

  const handleOnFloorChange = () => {
    form.setValue('locationId', '');
  }

  const handleOnAssetTypeChange = () => {
    form.setValue('subAssetTypeId', '');
  }

  return (
    <CoreForm form={form} onSubmit={handleOnSubmit} footer={readOnly ? null : undefined}>
      <CoreForm.Row>
        <PickerFieldController
          controlName='buildingId'
          title='Building'
          required
          disabled={readOnly}
          onChange={handleOnBuildingChange}
          defaultFirst={false}
          items={(buildings ?? []).map(item => ({
            label: item.name,
            value: item.id
          }))} />
        <PickerFieldController
          controlName='floorId'
          title='Floor'
          required
          disabled={readOnly}
          onChange={handleOnFloorChange}
          defaultFirst={false}
          items={floorOptions} />
        <PickerFieldController
          controlName='locationId'
          title='Location'
          required
          disabled={readOnly}
          defaultFirst={false}
          items={locationOptions} />
      </CoreForm.Row>
      <CoreForm.Row>
        <PickerFieldController
          controlName='assetTypeId'
          title='Asset Type'
          required
          disabled={readOnly}
          onChange={handleOnAssetTypeChange}
          defaultFirst={false}
          items={(assetTypes ?? []).map(item => ({
            label: item.name,
            value: item.id
          }))} />
        <PickerFieldController
          controlName='subAssetTypeId'
          title='Sub Asset Type'
          required
          disabled={readOnly}
          defaultFirst={false}
          items={subAssetTypeOptions} />
        <TextFieldController controlName='description' title='Description' disabled={readOnly} />
      </CoreForm.Row>
      <CoreForm.Row>
        <TextFieldController controlName='uniqueReference' title='CoreRFID QR' disabled={readOnly} />
        <TextFieldController required controlName='code' title='Code' disabled={readOnly} />
        <PickerFieldController
          numeric
          controlName='priority'
          title='Ranking'
          required
          disabled={readOnly}
          items={assetPriorityOptions} />
      </CoreForm.Row>
      <CoreForm.Row>
        <TextFieldController
          style={{minHeight: 50}}
          multiline
          controlName='supportingInformation'
          title='Supporting Information'
          disabled={readOnly} />
      </CoreForm.Row>
      <CoreForm.Row>
        <TextFieldController
          style={{minHeight: 100}}
          multiline
          controlName='troubleshootingGuide'
          title='Troubleshooting Guide'
          disabled={readOnly} />
      </CoreForm.Row>
      <CoreForm.Row>
        <View style={styles.imageSection}>
          <View style={styles.imageContainer}>
            <Image
              style={{ height: 200, width: 200 }}
              resizeMode='cover'
              source={{
                uri: imageBase64 != "" ? imageBase64 : asset?.imageUri
              }}
            />
          </View>
          {!readOnly && (
            <Button
              icon="camera" mode='elevated'
              buttonColor='#FFF'
              textColor='#333'
              style={{marginTop: 12}}
              onPress={() => loadFile()}
            >
              Upload Picture
            </Button>
          )}
        </View>
      </CoreForm.Row>
    </CoreForm>
  )
}

const styles = StyleSheet.create({
  imageFormContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: "column"
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: "row",
    minHeight: 200,
  },
  imageSection: {
    borderWidth: 1,
    borderColor: "#d9d9d9",
    borderRadius: 6,
    padding: 12
  }
})
